import { useEffect, useState } from 'react';
import { ERROR_MESSAGE_WEATHER_MODULE__500 } from '../../../../../configurations/error-messages';
import { callEndpoint, telemetry } from '../../../../../services/backend';
import {
  safelyFillHourlyGapsForWeatherGraphType,
  type WeatherForecastGraphApiResponse,
  type WeatherForecastGraphLocation,
  type WeatherForecastGraphPoint,
} from '../../helpers/graph/graphData';
import { FREE_FORECAST_DAYS } from '../../helpers/forecastHorizon';

const hasValidCoordinates = (
  latitude: number | undefined,
  longitude: number | undefined,
) =>
  latitude != null &&
  longitude != null &&
  Number.isFinite(latitude) &&
  Number.isFinite(longitude);

export interface WeatherForecastGraphDataState {
  windPoints: WeatherForecastGraphPoint[];
  swellPoints: WeatherForecastGraphPoint[];
  swellPeriodPoints: WeatherForecastGraphPoint[];
  tidePoints: WeatherForecastGraphPoint[];
  tideFooterText: string | undefined;
  rainfallPoints: WeatherForecastGraphPoint[];
  temperaturePoints: WeatherForecastGraphPoint[];
  uvPoints: WeatherForecastGraphPoint[];
  location: WeatherForecastGraphLocation | undefined;
  isLoading: boolean;
  error: string | null;
}

const emptyState: WeatherForecastGraphDataState = {
  windPoints: [],
  swellPoints: [],
  swellPeriodPoints: [],
  tidePoints: [],
  tideFooterText: undefined,
  rainfallPoints: [],
  temperaturePoints: [],
  uvPoints: [],
  location: undefined,
  isLoading: true,
  error: null,
};

const GRAPH_SERIES_TYPES = [
  'wind',
  'swell-height',
  'swell-period',
  'tides',
  'rainfall',
  'temperature',
  'uv',
] as const;

const resolveGraphLocation = (
  responses: Array<WeatherForecastGraphApiResponse | undefined>,
): WeatherForecastGraphLocation | undefined => {
  const locationWithTimeZone = responses
    .map((response) => response?.location)
    .find((location) => location?.timeZone);

  if (!locationWithTimeZone?.timeZone) {
    return undefined;
  }

  return {
    name: locationWithTimeZone.name,
    distance: locationWithTimeZone.distance,
    distanceUnits: locationWithTimeZone.distanceUnits,
    timeZone: locationWithTimeZone.timeZone,
    utcOffset: locationWithTimeZone.utcOffset,
  };
};

export const useWeatherForecastGraphData = (
  latitude: number | undefined,
  longitude: number | undefined,
  days = FREE_FORECAST_DAYS,
): WeatherForecastGraphDataState => {
  const [state, setState] = useState<WeatherForecastGraphDataState>(emptyState);

  useEffect(() => {
    let isMounted = true;

    if (!hasValidCoordinates(latitude, longitude)) {
      setState({ ...emptyState, isLoading: false });
      return () => {
        isMounted = false;
      };
    }

    setState({ ...emptyState, isLoading: true });

    const loadGraphData = async () => {
      try {
        const queryParams = {
          latitude: latitude as number,
          longitude: longitude as number,
          days,
        };

        const fetchSeries = (type: string) =>
          callEndpoint(
            callEndpoint.endpoints.queryWeatherForecastGraphData,
            {},
            { ...queryParams, type },
          ) as Promise<WeatherForecastGraphApiResponse>;

        const settledResults = await Promise.allSettled(
          GRAPH_SERIES_TYPES.map((type) => fetchSeries(type)),
        );

        if (!isMounted) {
          return;
        }

        settledResults.forEach((result, index) => {
          if (result.status === 'rejected') {
            telemetry(
              telemetry.ERROR,
              `[useWeatherForecastGraphData.loadGraphData] ${GRAPH_SERIES_TYPES[index]} failed: ${JSON.stringify(result.reason)}`,
            );
          }
        });

        const getResponse = (
          index: number,
        ): WeatherForecastGraphApiResponse | undefined => {
          const result = settledResults[index];
          return result.status === 'fulfilled' ? result.value : undefined;
        };

        const responses = GRAPH_SERIES_TYPES.map((_, index) =>
          getResponse(index),
        );

        const anySucceeded = settledResults.some(
          (result) => result.status === 'fulfilled',
        );

        if (!anySucceeded) {
          setState({
            ...emptyState,
            isLoading: false,
            error: ERROR_MESSAGE_WEATHER_MODULE__500,
          });
          return;
        }

        const [
          windResponse,
          swellResponse,
          swellPeriodResponse,
          tideResponse,
          rainfallResponse,
          temperatureResponse,
          uvResponse,
        ] = responses;

        const location = resolveGraphLocation(responses);
        const stationTimeZone = location?.timeZone;

        setState({
          windPoints: safelyFillHourlyGapsForWeatherGraphType(
            windResponse?.points,
            'wind',
            stationTimeZone,
          ),
          swellPoints: safelyFillHourlyGapsForWeatherGraphType(
            swellResponse?.points,
            'swell-height',
            stationTimeZone,
          ),
          swellPeriodPoints: swellPeriodResponse?.points ?? [],
          tidePoints: tideResponse?.points ?? [],
          tideFooterText: tideResponse?.footerText,
          rainfallPoints: rainfallResponse?.points ?? [],
          temperaturePoints: temperatureResponse?.points ?? [],
          uvPoints: uvResponse?.points ?? [],
          location,
          isLoading: false,
          error: null,
        });
      } catch (loadError) {
        telemetry(
          telemetry.ERROR,
          `[useWeatherForecastGraphData.loadGraphData] ${JSON.stringify(loadError)}`,
        );

        if (isMounted) {
          setState({
            ...emptyState,
            isLoading: false,
            error: ERROR_MESSAGE_WEATHER_MODULE__500,
          });
        }
      }
    };

    loadGraphData();

    return () => {
      isMounted = false;
    };
  }, [days, latitude, longitude]);

  return state;
};
